import { Injectable, inject } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { PdfUtilsServiceV2 } from '@src/app/core/services/helpers/pdf-utils/pdf-utils-v2.service';
import { TableExportService } from '@src/app/shared/components/table/services/table-export.service';
import {
  DFP_SCALE_SHEET_NOTE,
  DFP_SCALE_TITLE_NOTE_MIXED_UNITS,
  DfpValueScale,
} from '../models/dfp-value-scale.model';

/** Uma aba da planilha: o nome da aba e o seletor da tabela renderizada de onde as linhas saem. */
interface DfpSheetConfig {
  name: string;
  selector: string;
}

/**
 * Exportação da tela de DFP Histórica (PDF e XLSX).
 *
 * Extraído do `dfp.component` porque o componente estava no teto de ~1.000 linhas do `AGENTS.md` — e este
 * bloco não depende de nada do estado da tela além do nome da empresa, que entra por parâmetro.
 *
 * **Lê do DOM de propósito.** A planilha sai do que está renderizado (`<thead>`/`<tbody>` das tabelas), não
 * dos arrays de dados: é assim que o export respeita as colunas que o usuário adicionou/removeu e os anos
 * que ele ocultou, sem reimplementar aqui as regras de montagem de coluna que as tabelas já aplicam.
 */
@Injectable({ providedIn: 'root' })
export class DfpExportService {
  private readonly pdfUtilsService = inject(PdfUtilsServiceV2);
  private readonly tableExportService = inject(TableExportService);

  /** Abas da planilha, na mesma ordem em que as tabelas aparecem na tela. */
  private readonly sheetsConfig: DfpSheetConfig[] = [
    {
      name: 'DRE',
      selector: '.performance-table--dre',
    },
    {
      name: 'Balanço',
      selector: '.performance-table--balance',
    },
    {
      name: 'Destaques',
      selector: '.performance-table--highlights',
    },
    {
      name: 'Faturamento',
      selector: '.performance-table--revenue',
    },
  ];

  /**
   * Itens do menu de exportação. Recebe o nome da empresa como **função**, não como valor: o menu é montado
   * uma vez no construtor do componente, e a empresa muda depois, a cada seleção no `app-select-company`.
   */
  buildMenuItems(companyName: () => string, valueScale: () => DfpValueScale): MenuItem[] {
    return [
      {
        label: 'Exportar PDF',
        icon: 'pi pi-file-pdf',
        command: () => this.exportToPdf(companyName(), valueScale()),
      },
      {
        label: 'Exportar Excel',
        icon: 'pi pi-file-excel',
        command: () => this.exportToExcel(valueScale()),
      },
    ];
  }

  /**
   * @param valueScale Escala em que a tela está exibindo os valores. Precisa vir por parâmetro porque o
   *   rótulo da TELA não chega no arquivo: o `<header>` de cada painel é `ignore-export`, e o gerador remove
   *   fisicamente os `.ignore-export` do clone antes de renderizar. Sem isto, um PDF em milhares sairia sem
   *   nenhuma indicação de unidade — e é justamente o arquivo que circula fora da tela.
   *
   *   Usa a redação de **unidade mista**: o PDF da página inteira inclui "Pontos de destaque", onde dias e
   *   múltiplos não são escalados, então "em milhares" sozinho seria impreciso para parte do documento.
   */
  exportToPdf(companyName: string, valueScale: DfpValueScale = 'original'): void {
    const scaleNote = DFP_SCALE_TITLE_NOTE_MIXED_UNITS[valueScale];
    const subtitle = scaleNote ? `${companyName} · ${scaleNote}` : companyName;

    this.pdfUtilsService
      .exportToPdf({
        className: '.dfp-historical',
        filename: 'dfp-historica.pdf',
        title: 'DFP Histórica',
        subtitle,
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Erro ao gerar PDF:', error);
      });
  }

  /**
   * @param valueScale Escala em que a tela está exibindo os valores. O PDF não precisa dela — é captura do
   *   DOM, e o título do painel já carrega "(em milhares)". A planilha precisa: ela é montada só a partir de
   *   `<thead>`/`<tbody>`, então o título do painel nunca chega lá e o arquivo sairia sem dizer a unidade.
   */
  exportToExcel(valueScale: DfpValueScale = 'original'): void {
    const scaleNote = DFP_SCALE_SHEET_NOTE[valueScale];

    const sheets = this.sheetsConfig
      .map(config => ({
        name: config.name,
        rows: this.withScaleNote(this.buildSheetRowsFromDom(config.selector), scaleNote),
      }))
      .filter(sheet => sheet.rows.length > 0);

    if (!sheets.length) {
      // eslint-disable-next-line no-console
      console.warn('Nenhuma tabela encontrada para exportação Excel da DFP.');
      return;
    }

    this.tableExportService.exportMultipleSheetsToXlsx(sheets, 'dfp-historica');
  }

  /**
   * Prefixa a aba com a nota de unidade, quando há escala aplicada. Aba vazia continua vazia — a nota sozinha
   * faria o `filter(sheet => sheet.rows.length > 0)` acima considerar como conteúdo uma aba sem nenhum dado.
   */
  private withScaleNote(
    rows: (string | number | boolean | null)[][],
    scaleNote: string | null
  ): (string | number | boolean | null)[][] {
    if (!scaleNote || rows.length === 0) {
      return rows;
    }

    return [[scaleNote], ...rows];
  }

  /**
   * Monta as linhas de uma aba a partir da tabela renderizada.
   *
   * Cada célula passa por `TableExportService.extractCellText`, e não por `textContent` cru, para que os
   * controles inline marcados com `ignore-export` (o "+" de período, o "×" da coluna, o lápis do ano) não
   * vazem como texto para dentro da planilha.
   */
  private buildSheetRowsFromDom(selector: string): (string | number | boolean | null)[][] {
    const rows: (string | number | boolean | null)[][] = [];
    const container = document.querySelector(selector) as HTMLElement | null;
    if (!container) {
      return rows;
    }

    const table = container.querySelector('table') as HTMLTableElement | null;
    if (!table) {
      return rows;
    }

    const allRows = [
      ...Array.from(table.querySelectorAll('thead tr') as NodeListOf<HTMLTableRowElement>),
      ...Array.from(table.querySelectorAll('tbody tr') as NodeListOf<HTMLTableRowElement>),
    ];

    allRows.forEach(tr => {
      rows.push(
        Array.from(tr.cells).map((cell: HTMLTableCellElement) =>
          this.tableExportService.extractCellText(cell)
        )
      );
    });

    return rows;
  }
}
